import { sum0, roundn } from "@js/utils";

/**
 * Given an array of historical emissions and a total budget this function, for a given year,
 * calculates the remaining budget and the emissions used so far.
 * @param {Array} emissions
 * @param {Object} time_axis
 * @param {Number} current_year
 * @param {Object} budget
 * @returns {Array} [lastemission, used, remainingbudget]
 */
export function usedremaining(emissions, time_axis, current_year, budget) {
  const history = gethistory(emissions, time_axis.first_year, current_year);

  const [, remainingbudget] = rebase(
    time_axis.first_year,
    budget.year,
    emissions,
    budget.tCO2,
    current_year
  );
  const lastemission = last(history);
  const used = sum0(history);

  return [lastemission, used, remainingbudget];
}

function autorate(lastemission, remaining_tons) {
  let rate = 0;
  if (remaining_tons > 0) {
    rate = Math.exp(-lastemission / remaining_tons);
    //rate = 0.82 would mean decrease 18% yearly to not spend remaining_tons (ever)
  }

  // TODO: Isn't this the correct way to calculate a geometric series?
  // let rate = 1 - lastemission/remaining_tons
  // let rate = 1 - lastemission/(remaining_tons + lastemission)
  
  return rate;
}

/**
 * Given initial conditions calculates the required yearly decrease in emissions (%)
 * for which the remaining tons are not exceeded ever.
 * @param {Number} lastemission 
 * @param {Number} remaining_tons 
 * @returns {Number} yearly emission decrease (%)
 */
export function autopercent(lastemission, remaining_tons) {
  return percentFromRate(autorate(lastemission, remaining_tons));
}

export function autorate_tyndall(lastemission, remaining_tons) {
  //tyndall uses an excel built in function (goalseek)
  //to find a percentage low enough to not consume budget within the next 80 years.

  //this is a crude implementation in js of the same idea. increasing reduction by 0.1% each iteration
  //goal is to produce same values as the tyndall excel sheets.
  for (let r = 1; r > 0.5; r -= 0.001) {
    let consumed2020to2100 = sum0(
      rangef(80, (i) => lastemission * Math.pow(r, i + 1))
    );
    if (consumed2020to2100 < remaining_tons) {
      return r;
    }
  }
  return 0.5;
}

export function autopercent_tyndall(lastemission, remaining_tons) {
  return percentFromRate(autorate_tyndall(lastemission, remaining_tons));
}

export function scenarios(
  emissions,
  budget,
  scenariostartyear,
  time_axis,
  slidervalues
) {
  return emissions.map(
    (em, i) =>
      scenario(em, budget, scenariostartyear, time_axis, slidervalues[i] / 100)
        .track
  );
}

/**
 * 
 * @param {Array<Number>} emissions padded with emissions last year, in order to match time_axis
 * @param {{ year: Number, tCO2: Number }} budget 
 * @param {Number} current_year 
 * @param {{ first_year: Number, last_year: Number}} time_axis 
 * @param {Number} rate 
 * @returns 
 */
export function scenario(emissions, budget, current_year, time_axis, rate) {
  let history = gethistory(emissions, time_axis.first_year, current_year);
  let [, remaining] = rebase(
    time_axis.first_year,
    budget.year,
    emissions,
    budget.tCO2,
    current_year
  );
  let future = percentscenario(history, time_axis, rate);
  let track = history.concat(future);

  let leftover = Math.max(0, remaining - sum0(future));
  return { track, leftover };
}

function gethistory(h, first_year, current_year) {
  let passed_years = Math.max(0, current_year - first_year);
  return h.slice(0, passed_years);
}

/**
 * Given historical emissions and a budget for a specific year this function
 * calculates the actual budget for another arbitrary year.
 * @param {Number} first_year
 * @param {Number} budget_year
 * @param {Array} h
 * @param {Number} budget
 * @param {Number} current_year
 * @returns {Array} [used, remainingbudget]
 */
export function rebase(first_year, budget_year, h, budget, current_year) {
  let passed_years = Math.max(0, current_year - first_year);
  let contextyears = budget_year - first_year;
  let used = 0;
  for (let i = passed_years; i < Math.min(h.length, contextyears); i++) {
    used -= h[i];
  }
  for (let i = contextyears; i < Math.min(h.length, passed_years); i++) {
    used += h[i];
  }
  let remainingbudget = budget - used;
  return [used, remainingbudget];
}

/**
 * This is the very function that calculates (simulates) a future scenario 
 * given a history of emissions, a specified time axis and a chosen rate of change.
 * @param {Array<Number>} history 
 * @param {{ first_year: Number, last_year: Number }} time_axis 
 * @param {Number} rate rate = -0.1 gives r=0.9, rate=0.2 gives r=1.2
 * @returns 
 */
export function percentscenario(history, time_axis, rate) {
  //rate = -0.1 gives r=0.9, rate=0.2 gives r=1.2
  const r = 1 + rate;
  const Nyears = time_axis.last_year - time_axis.first_year + 1;
  const h1 = last(history);
  const futurelen = Nyears - history.length;
  let future = rangef(futurelen, (i) => h1 * Math.pow(r, i + 1));
  return future;
}

function last(a) {
  return a[a.length - 1];
}

function rangef(n, func) {
  const a = new Array(n);
  for (let i = 0; i < n; i++) {
    a[i] = func(i);
  }
  return a;
}

///////////////////////////////////////
// Scenario for periods rather than

function periodrateFromRate(rate, Nyears) {
  // rate^Nyears = periodrate
  return Math.pow(rate, Nyears);
}

export function periodpercentFromPercent(percent, Nyears) {
  //return what a change in percent for Nyears corresponds to
  // rate^Nyears = periodrate
  const rate = rateFromPercent(percent);
  const totalrate = periodrateFromRate(rate, Nyears);
  return percentFromRate(totalrate);
}

function rateFromPercent(percent) {
  return percent / 100 + 1;
}

function percentFromRate(rate) {
  return 100 * (rate - 1);
}

export function yearindexFromYear(year, time_axis) {
  return year - time_axis.first_year;
}

export function yearFromYearindex(yearindex, time_axis) {
  return time_axis.first_year + yearindex;
}

//year when only budget has been fully consumed
export function budgetemptiedyear(
  percent,
  emissions,
  currentyear,
  time_axis,
  remainingbudget
) {
  const r = rateFromPercent(percent);
  let lastemission = emissions[yearindexFromYear(currentyear - 1, time_axis)];
  let consumed = 0;
  let y = lastemission;
  let i = -1;

  while (consumed < remainingbudget) {
    y *= r;
    consumed += y;
    i++;
    if (i > 100) {
      break;
    }
  }

  return [currentyear + i, i];
}

//year when 90% of budget has been consumed (less than 10% remains)
export function budgetfractionconsumedyear(
  percent,
  emissions,
  currentyear,
  time_axis,
  remainingbudget,
  maxfraction = 0.9 //default 90%
) {
  const r = rateFromPercent(percent);
  let lastemission = emissions[yearindexFromYear(currentyear - 1, time_axis)];
  let consumed = 0;
  let y = lastemission;
  let i = -1;

  while (consumed / remainingbudget < maxfraction) {
    y *= r;
    consumed += y;
    i++;
    if (i > 1000) {
      console.log("at rate ", r, ", ", maxfraction, "will not be reached");
      console.log(
        "after 1000 years: fraction consumed becomes ",
        consumed / remainingbudget
      );
      break;
    }
  }

  return [currentyear + i, i];
}

export function initialperiods(autopercent, emissions, time_axis) {
  let period0 = {
    time_axis: { first_year: 2020, last_year: 2024 },
  };

  let period1 = {
    time_axis: { first_year: 2025, last_year: 2029 },
  };

  let period2 = {
    time_axis: { first_year: 2030, last_year: 2034 },
  };

  let period3 = {
    time_axis: { first_year: 2035, last_year: 2039 },
  };

  let period4 = {
    time_axis: { first_year: 2040, last_year: 2044 },
  };
  let period5 = {
    time_axis: { first_year: 2045, last_year: 2100 },
  };

  let periods = [period0, period1, period2, period3, period4, period5];

  for (let n = 0; n < periods.length; n++) {
    periods[n].yearlypercent = autopercent;
    periods[n].tCO2 = roundn(periodstons(emissions, time_axis, periods[n]));
    periods[n].periodpercent = roundn(
      periodpercentFromPercent(autopercent, 5),
      1
    );
  }
  return periods;
}

export function periodstons(emissions, time_axis, period) {
  const a = yearindexFromYear(period.time_axis.first_year, time_axis);
  const b = yearindexFromYear(period.time_axis.last_year, time_axis);
  return sum0(emissions.slice(a, b + 1));
}
